import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { InfiniteData } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { ReviewsInsight } from 'apis/merchant-gateway/insights/reviews/types';
import { RowType } from 'modules/insights-v2/types';
import { formatPercentage } from 'modules/insights-v2/utils';

import OffersSummaryBanner from './OffersSummaryBanner';
import { useSentOffers } from './useCompensationOffers';
import ErrorView from '../../ErrorView';
import InsightMetricRow from '../../InsightSummary/InsightMetricRow';
import LoadingView from '../../LoadingView';

type CompensationOffersSummaryProps = {
  venueId: string;
  timezone: string;
  reviewsData: InfiniteData<ReviewsInsight> | undefined;
  onFilterBadReviews: () => void;
};

const ErrorContainer = styled.div`
  padding: 16px;
`;

const Row = styled.div`
  padding-inline: 16px;
  border-block-end: 1px solid ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  min-height: 56px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:last-child {
    border-block-end: none;
  }
`;

const CompensationOffersSummary = ({
  venueId,
  timezone,
  reviewsData,
  onFilterBadReviews,
}: CompensationOffersSummaryProps) => {
  const { locale } = useIntl();

  const reviews = useMemo(() => {
    if (!reviewsData) {
      return [];
    }
    return reviewsData.pages.reduce(
      (pagesRes, page) =>
        page.data.reviews.reduce((res, item) => [...res, { ...item, id: item.purchaseId }], pagesRes) ?? pagesRes,
      [],
    );
  }, [reviewsData]);
  const purchaseIds = useMemo(() => reviews.map(review => review.purchaseId), [reviews]);

  const {
    data: sentOffers = [],
    isFetching,
    isError,
    refetch,
  } = useSentOffers({
    venueId,
    purchaseIds,
    isEnabled: true, // checked already in parent component
  });

  if (isFetching) {
    return <LoadingView />;
  }

  if (isError) {
    return (
      <ErrorContainer>
        <ErrorView onRetry={refetch} />
      </ErrorContainer>
    );
  }

  const sentOffersCount = sentOffers.length;
  const claimedOffersCount = sentOffers.filter(offer => offer.claimed_timestamp !== undefined).length;

  return (
    <>
      <Row>
        <InsightMetricRow
          data={{
            type: RowType.Metric,
            title: 'insights.ratings-and-reviews.offers-summary.offers-sent-count',
            metrics: sentOffersCount,
          }}
        />
      </Row>
      <Row>
        <InsightMetricRow
          data={{
            type: RowType.Metric,
            title: 'insights.ratings-and-reviews.offers-summary.offers-claimed-count',
            metrics: claimedOffersCount,
            growthRate:
              sentOffersCount > 0 ? formatPercentage(claimedOffersCount / sentOffersCount, locale) : undefined,
            growthTrend: sentOffersCount > 0 ? 'positive' : undefined,
          }}
        />
      </Row>
      <OffersSummaryBanner
        timezone={timezone}
        reviews={reviews}
        sentOffers={sentOffers}
        onFilterBadReviews={onFilterBadReviews}
      />
    </>
  );
};

export default CompensationOffersSummary;
